import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, MessageSquare, BarChart3, Rocket, ShieldCheck, ArrowRight,
  Zap, FileText, Send, ChartBar, CheckCircle2, RefreshCw, Bell, Star,
  Users, Award,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FeatureInfographic from "@/components/FeatureInfographic";
import SectionInfographicGrid from "@/components/SectionInfographicGrid";

const TILE = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition";
const TAG = "inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold";

const LOOP = [
  { n: "1", title: "Create", desc: "AI drafts a survey from your goal. You edit every word." },
  { n: "2", title: "Send",   desc: "Email, SMS, WhatsApp, link, QR, kiosk or website widget." },
  { n: "3", title: "Collect", desc: "Responses arrive with sentiment, themes, and PII redacted." },
  { n: "4", title: "Analyze", desc: "CSAT · NPS · CES · themes · urgent alerts, all facts vs interpretations." },
  { n: "5", title: "Recommend", desc: "AI proposes prioritized website and business improvements." },
  { n: "6", title: "Approve", desc: "You control what publishes — never automatic." },
  { n: "7", title: "Improve", desc: "Approved changes go live as a CMS draft with rollback." },
  { n: "8", title: "Measure Again", desc: "Rerun the survey. See the delta. Repeat." },
];

const CAPABILITIES = [
  { icon: Sparkles, title: "AI survey drafter", desc: "Describe what you want to learn — get a full survey with invitation copy, question logic, and quality score." },
  { icon: MessageSquare, title: "Multi-channel delivery", desc: "Email, SMS, WhatsApp, link, QR code, on-site widget, kiosk, or API-triggered." },
  { icon: BarChart3, title: "Fact-vs-interpretation insights", desc: "Every AI insight labels the measured result, the interpretation, the suggested action, and the confidence level." },
  { icon: Bell, title: "Alerts that respect people", desc: "Low scores, detractors, and safety/fraud/discrimination keywords open a service-recovery case." },
  { icon: Award, title: "Testimonials with consent", desc: "Only approved comments go to the website publishing queue — never automatic." },
  { icon: ShieldCheck, title: "Privacy first", desc: "Anonymous mode ON by default. PII redacted in analysis. Contact export is owner-only." },
];

const INDUSTRIES = [
  "General customer satisfaction", "Post-purchase", "Service completion",
  "Website experience", "Support resolution", "Appointment / consultation",
  "Restaurant & catering", "Medical office", "Dental office",
  "Automotive dealership", "Automotive service", "Plumber",
  "Electrician", "Contractor", "Hotel & resort", "Travel",
  "Taxi & transport", "Real estate", "Ecommerce delivery",
  "Product returns", "Event", "Product feedback",
  "Lost customer", "Membership", "Nonprofit / donor",
];

const PACKAGES = [
  { key: "starter", name: "Feedback Starter",
    for: "Small local businesses",
    features: [
      "One active survey",
      "Link / QR / website widget",
      "Basic dashboard", "Monthly summary",
      "CSV export",
    ]},
  { key: "growth", name: "Feedback Growth", highlight: true,
    for: "Growing businesses",
    features: [
      "Multiple surveys",
      "AI creation + analysis",
      "Email automation, alerts",
      "PDF + Excel reports",
      "Testimonial workflow",
      "Website publishing queue",
    ]},
  { key: "pro", name: "Feedback Pro",
    for: "Multi-location clients",
    features: [
      "Location comparisons",
      "CRM & ecommerce integrations",
      "Scheduled reports",
      "SMS + WhatsApp",
      "Service-recovery workflow",
      "Advanced permissions",
    ]},
  { key: "managed", name: "Managed Customer Experience",
    for: "Fully-managed clients",
    features: [
      "We create + run the campaigns",
      "We review results with you",
      "Quarterly recommendations",
      "We draft website updates",
      "We measure results after changes",
    ]},
];

export default function AiFeedbackPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1" data-testid="ai-feedback-page">
        {/* Hero */}
        <section className="bg-gradient-to-b from-emerald-950 via-slate-900 to-slate-950 text-white pt-28 pb-20">
          <div className="max-w-6xl mx-auto px-6">
            <span className={`${TAG} bg-emerald-900/50 border-emerald-700/50 text-emerald-200`} data-testid="fb-hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Turn feedback into measurable improvements
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              WebsiteTech AI Feedback Assistant
            </h1>
            <p className="mt-3 text-lg text-emerald-300 italic">Customer Voice AI</p>
            <p className="mt-6 text-lg text-slate-300 max-w-3xl">
              Tell our AI what you want to learn. It drafts the survey, helps you send it,
              analyzes the responses, recommends improvements — and with your approval,
              updates your website. Then it measures whether the changes worked.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/feedback" data-testid="hero-configure-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 px-5 py-3 font-semibold transition">
                Start your first survey <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/audit" data-testid="hero-audit-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-3 font-semibold transition">
                Get a free website audit
              </Link>
            </div>
            <p className="mt-6 text-xs text-slate-400 max-w-2xl">
              Anonymous by default · PII redacted in analysis · Nothing auto-published.
              Consent-first. Review-gating is blocked in the platform.
            </p>
          </div>
        </section>

        {/* Loop */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-emerald-700">The Continuous Improvement Loop</h2>
            <p className="mt-2 text-3xl font-bold text-slate-900 max-w-3xl">Create → Send → Collect → Analyze → Recommend → Approve → Improve → Measure Again.</p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {LOOP.map((s) => (
                <div key={s.n} className="relative rounded-xl border border-slate-200 p-5 bg-slate-50" data-testid={`loop-step-${s.n}`}>
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-emerald-600 text-white grid place-items-center font-bold text-sm">{s.n}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{s.title}</div>
                  <div className="mt-2 text-xs text-slate-600 leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>

            {/* AI-generated infographic */}
            <div className="mt-14">
              <FeatureInfographic slug="feedback" accent="#059669" />
              <SectionInfographicGrid slug="feedback" accent="#059669" />
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">What you get</h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {CAPABILITIES.map((c) => (
                <div key={c.title} className={TILE} data-testid={`capability-${c.title.split(' ')[0].toLowerCase()}`}>
                  <c.icon className="w-6 h-6 text-emerald-600" />
                  <div className="mt-4 font-semibold text-slate-900">{c.title}</div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">25 ready-to-run survey templates</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">Each one is editable. Each one has a "Make this fit my business" AI action.</p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2">
              {INDUSTRIES.map((n) => (
                <div key={n} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid={`industry-${n.split(' ')[0].toLowerCase()}`}>
                  {n}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">Four ways to buy</h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {PACKAGES.map((p) => (
                <div key={p.key} className={`rounded-2xl border p-6 flex flex-col ${p.highlight ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 bg-white"}`} data-testid={`package-${p.key}`}>
                  {p.highlight && <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-2">Most popular</div>}
                  <div className="text-lg font-bold text-slate-900">{p.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{p.for}</div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/feedback" className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 px-4 py-3 font-semibold transition">
                    Get started <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-b from-emerald-950 to-slate-950 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <MessageSquare className="w-12 h-12 text-emerald-400 mx-auto" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold">Improve the website. Delight customers. Prove the results.</h2>
            <Link to="/feedback" data-testid="final-cta-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 px-6 py-4 font-semibold text-lg transition">
              Draft your first survey — 60 seconds <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
